import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { isReservedCustomerPathSegment } from '../auth/customerAuthPaths.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';

export interface CustomerPollFeatureFlagsSnapshot {
  readonly giftSurfacesMaster: boolean;
  readonly giftDonate: boolean;
  readonly giftRecurring: boolean;
  readonly giftMyDonations: boolean;
  /** Wave 4 — null ⇒ TEST economy chrome; ISO ⇒ LIVE (hide banner). */
  readonly livePublishedAt: string | null;
  /** Self-Scan selective-check capability (FR-16 / FR-17). Default OFF. */
  readonly tenantSelfScanSelectiveCheckEnabled: boolean;
  /** Sample rate % when selective check is ON. */
  readonly samplePercent: number;
  readonly revision: number;
}

interface CustomerFeatureFlagsApiResponse {
  readonly giftSurfacesMaster: boolean;
  readonly giftDonate: boolean;
  readonly giftRecurring: boolean;
  readonly giftMyDonations: boolean;
  readonly livePublishedAt?: string | null;
  readonly tenantSelfScanSelectiveCheckEnabled?: boolean;
  readonly samplePercent?: number;
  readonly revision: number;
}

export function useCustomerFeatureFlagsQuery(): UseQueryResult<
  CustomerPollFeatureFlagsSnapshot,
  Error
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();

  return useQuery({
    queryKey: customerQueryKeys.featureFlags(tenantCode),
    queryFn: async (): Promise<CustomerPollFeatureFlagsSnapshot> => {
      const data = await api.request<CustomerFeatureFlagsApiResponse>('/customer/feature-flags');
      return {
        giftSurfacesMaster: data.giftSurfacesMaster,
        giftDonate: data.giftDonate,
        giftRecurring: data.giftRecurring,
        giftMyDonations: data.giftMyDonations,
        livePublishedAt: data.livePublishedAt ?? null,
        tenantSelfScanSelectiveCheckEnabled:
          data.tenantSelfScanSelectiveCheckEnabled === true,
        samplePercent:
          typeof data.samplePercent === 'number' &&
          Number.isFinite(data.samplePercent) &&
          data.samplePercent >= 1 &&
          data.samplePercent <= 100
            ? Math.trunc(data.samplePercent)
            : 10,
        revision: data.revision,
      };
    },
    // G6 — never poll `/api/shops|favorites|…/v1/customer/feature-flags`.
    enabled:
      !isPlatformChromeTenant(tenantCode) &&
      !isReservedCustomerPathSegment(tenantCode) &&
      hasCustomerApiTenant(tenantCode),
    staleTime: 45_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}
